import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';
import {
  ILessonNote,
  ILessonNoteFilters,
  ILessonNoteApiResponse,
  ILessonNoteDetailResponse,
  ILessonNoteSummary,
  ILearningIndicatorApiResponse,
  ILessonCompetencyApiResponse,
  ILessonAssignmentApiResponse,
  IOutlineApiResponse,
  ICompetencyApiResponse,
  IAssignmentApiResponse,
  ICreateLessonNotePayload,
  ILessonNoteImportProposal,
  IImportProposalParams,
} from '../../Shared/types/lessonNotes';

export const LessonNotesApi = createApi({
  reducerPath: 'LessonNotesApi',
  baseQuery,
  tagTypes: [
    'LessonNote',
    'Outline',
    'Competency',
    'Assignment',
    'Resource',
    'LearningIndicator',
    'LessonIndicator',
  ],
  endpoints: builder => ({
    // Lesson Notes CRUD
    getLessonNotes: builder.query<ILessonNoteApiResponse, ILessonNoteFilters>({
      query: filters => {
        const queryParams = new URLSearchParams();
        if (filters.page) queryParams.append('page', filters.page.toString());
        if (filters.pageSize)
          queryParams.append('page_size', filters.pageSize.toString());
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.subjectId)
          queryParams.append('subject_id', filters.subjectId.toString());
        if (filters.classroomId)
          queryParams.append('classroom_id', filters.classroomId.toString());
        if (filters.sessionId)
          queryParams.append('session_id', filters.sessionId.toString());
        if (filters.teacherId)
          queryParams.append('teacher_id', filters.teacherId.toString());
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.dateFrom) queryParams.append('date_from', filters.dateFrom);
        if (filters.dateTo) queryParams.append('date_to', filters.dateTo);

        return `/academics/lesson-notes?${queryParams.toString()}`;
      },
      providesTags: ['LessonNote'],
    }),

    getLessonNoteById: builder.query<ILessonNoteDetailResponse, number>({
      query: id => `/academics/lesson-notes/${id}`,
      providesTags: ['LessonNote'],
    }),

    createLessonNote: builder.mutation<
      ILessonNoteDetailResponse,
      ICreateLessonNotePayload
    >({
      query: lessonNoteData => ({
        url: '/academics/lesson-notes',
        method: 'POST',
        body: lessonNoteData,
      }),
      invalidatesTags: ['LessonNote'],
    }),

    updateLessonNote: builder.mutation<
      ILessonNoteDetailResponse,
      { id: number; data: Partial<ILessonNote> }
    >({
      query: ({ id, data }) => ({
        url: `/academics/lesson-notes/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['LessonNote'],
    }),

    patchLessonNote: builder.mutation<
      ILessonNoteDetailResponse,
      {
        id: number;
        data:
          | Partial<ILessonNote>
          | {
              content?: { [sectionKey: string]: string };
              week_number?: number;
              lesson_number?: number;
              [key: string]: any;
            };
      }
    >({
      query: ({ id, data }) => ({
        url: `/academics/lesson-notes/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['LessonNote'],
    }),

    copyLessonNote: builder.mutation<
      ILessonNoteDetailResponse,
      {
        id: number;
        data: {
          targetWorkSchemeId: number;
          date: string;
          weekNumber?: number;
          lessonNumber?: number;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/academics/lesson-notes/${id}/copy`,
        method: 'POST',
        body: {
          target_work_scheme_id: data.targetWorkSchemeId,
          date: data.date,
          week_number: data.weekNumber,
          lesson_number: data.lessonNumber,
        },
      }),
      invalidatesTags: ['LessonNote'],
    }),

    // New endpoint specifically for updating lesson note content sections
    updateLessonNoteContent: builder.mutation<
      ILessonNoteDetailResponse,
      {
        id: number;
        content: { [sectionKey: string]: string };
        week_number?: number;
        lesson_number?: number;
      }
    >({
      query: ({ id, content, week_number, lesson_number }) => {
        const body: any = { content };
        if (week_number !== undefined) body.week_number = week_number;
        if (lesson_number !== undefined) body.lesson_number = lesson_number;

        return {
          url: `/academics/lesson-notes/${id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['LessonNote'],
    }),

    deleteLessonNote: builder.mutation<void, number>({
      query: id => ({
        url: `/academics/lesson-notes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LessonNote'],
    }),

    // Lesson Notes Summary
    getLessonNotesSummary: builder.query<
      ILessonNoteSummary,
      { workSchemeId?: number | string; sessionId?: number | string } | void
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params?.workSchemeId)
          queryParams.append('work_scheme_id', params.workSchemeId.toString());
        if (params?.sessionId)
          queryParams.append('session_id', params.sessionId.toString());
        const queryString = queryParams.toString();
        return `/academics/lesson-notes/summary${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['LessonNote'],
    }),

    // Learning Indicators
    getLessonLearningIndicators: builder.query<
      ILearningIndicatorApiResponse,
      { lessonId: number; page?: number; pageSize?: number }
    >({
      query: ({ lessonId, page, pageSize }) => {
        const queryParams = new URLSearchParams();
        if (page) queryParams.append('page', page.toString());
        if (pageSize) queryParams.append('page_size', pageSize.toString());
        return `/academics/lesson-notes/${lessonId}/learning-indicators?${queryParams.toString()}`;
      },
      providesTags: (_result, _error, { lessonId }) => [
        { type: 'LearningIndicator', id: lessonId },
      ],
    }),

    addLessonLearningIndicator: builder.mutation<
      any,
      { lessonId: number; learningIndicatorId: number }
    >({
      query: ({ lessonId, learningIndicatorId }) => ({
        url: `/academics/lesson-notes/${lessonId}/learning-indicators`,
        method: 'POST',
        body: { learningIndicatorId: learningIndicatorId },
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'LearningIndicator', id: lessonId },
      ],
    }),

    removeLessonLearningIndicator: builder.mutation<
      void,
      { lessonId: number; id: number }
    >({
      query: ({ lessonId, id }) => ({
        url: `/academics/lesson-notes/${lessonId}/learning-indicators/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'LearningIndicator', id: lessonId },
      ],
    }),

    // Lesson Competencies
    getLessonCompetencies: builder.query<
      ILessonCompetencyApiResponse,
      { lessonId: number; page?: number; pageSize?: number }
    >({
      query: ({ lessonId, page, pageSize }) => {
        const queryParams = new URLSearchParams();
        if (page) queryParams.append('page', page.toString());
        if (pageSize) queryParams.append('page_size', pageSize.toString());
        return `/academics/lessons/${lessonId}/competencies?${queryParams.toString()}`;
      },
      providesTags: (_result, _error, { lessonId }) => [
        { type: 'Competency', id: lessonId },
      ],
    }),

    addLessonCompetency: builder.mutation<
      any,
      { lessonId: number; competencyId: number }
    >({
      query: ({ lessonId, competencyId }) => ({
        url: `/academics/lessons/${lessonId}/competencies`,
        method: 'POST',
        body: { competency_id: competencyId },
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'Competency', id: lessonId },
      ],
    }),

    removeLessonCompetency: builder.mutation<
      void,
      { lessonId: number; id: number }
    >({
      query: ({ lessonId, id }) => ({
        url: `/academics/lessons/${lessonId}/competencies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'Competency', id: lessonId },
      ],
    }),

    // Lesson Assignments
    getLessonAssignments: builder.query<
      ILessonAssignmentApiResponse,
      { lessonId: number; page?: number; pageSize?: number }
    >({
      query: ({ lessonId, page, pageSize }) => {
        const queryParams = new URLSearchParams();
        if (page) queryParams.append('page', page.toString());
        if (pageSize) queryParams.append('page_size', pageSize.toString());
        return `/academics/lesson-notes/${lessonId}/assignments?${queryParams.toString()}`;
      },
      providesTags: (_result, _error, { lessonId }) => [
        { type: 'Assignment', id: lessonId },
      ],
    }),

    addLessonAssignment: builder.mutation<
      any,
      { lessonId: number; assignmentId: number; score?: number }
    >({
      query: ({ lessonId, assignmentId, score }) => ({
        url: `/academics/lesson-notes/${lessonId}/assignments`,
        method: 'POST',
        body: {
          assignment_id: assignmentId,
          ...(score !== undefined && { score }),
        },
      }),
      invalidatesTags: ['Assignment'],
    }),

    removeLessonAssignment: builder.mutation<
      void,
      { lessonId: number; id: number }
    >({
      query: ({ lessonId, id }) => ({
        url: `/academics/lesson-notes/${lessonId}/assignments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'Assignment', id: lessonId },
      ],
    }),

    // Lesson Resources
    getLessonResources: builder.query<
      any,
      { lessonId: number; page?: number; pageSize?: number }
    >({
      query: ({ lessonId, page, pageSize }) => {
        const queryParams = new URLSearchParams();
        if (page) queryParams.append('page', page.toString());
        if (pageSize) queryParams.append('page_size', pageSize.toString());
        return `/academics/lesson-notes/${lessonId}/resources?${queryParams.toString()}`;
      },
      providesTags: (_result, _error, { lessonId }) => [
        { type: 'Resource', id: lessonId },
      ],
    }),

    addLessonResource: builder.mutation<
      any,
      {
        lessonId: number;
        resourceId?: number;
        displayName?: string;
        url?: string;
        formData?: FormData;
      }
    >({
      query: ({ lessonId, resourceId, displayName, url, formData }) => {
        // Handle file upload with FormData
        if (formData) {
          return {
            url: `/academics/lesson-notes/${lessonId}/resources`,
            method: 'POST',
            body: formData,
          };
        }

        // Handle URL or existing resource
        const body: any = {};
        if (resourceId) {
          body.resource_id = resourceId;
        } else {
          // For new resources, include both displayName and url if provided
          if (displayName) {
            body.displayName = displayName;
          }
          if (url) {
            body.url = url;
          }
        }

        return {
          url: `/academics/lesson-notes/${lessonId}/resources`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'Resource', id: lessonId },
      ],
    }),

    removeLessonResource: builder.mutation<
      void,
      { lessonId: number; id: number }
    >({
      query: ({ lessonId, id }) => ({
        url: `/academics/lesson-notes/${lessonId}/resources/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'Resource', id: lessonId },
      ],
    }),

    // PATCH endpoint for updating lesson resources
    patchLessonResource: builder.mutation<
      any,
      { lessonId: number; id: number; displayName: string[] }
    >({
      query: ({ lessonId, id, displayName }) => ({
        url: `/academics/lesson-notes/${lessonId}/resources/${id}`,
        method: 'PATCH',
        body: { displayName: displayName },
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'Resource', id: lessonId },
      ],
    }),

    // PATCH endpoint for updating lesson assignments
    patchLessonAssignment: builder.mutation<
      any,
      { lessonId: number; id: number; assignmentId: number }
    >({
      query: ({ lessonId, id, assignmentId }) => ({
        url: `/academics/lesson-notes/${lessonId}/assignments/${id}`,
        method: 'PATCH',
        body: { assignment_id: assignmentId },
      }),
      invalidatesTags: (_result, _error, { lessonId }) => [
        { type: 'Assignment', id: lessonId },
      ],
    }),

    // Outlines (for reference)
    getOutlines: builder.query<IOutlineApiResponse, { search?: string }>({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params.search) queryParams.append('q', params.search);
        return `/lms/outlines?${queryParams.toString()}`;
      },
      providesTags: ['Outline'],
    }),

    // Competencies (for reference)
    getCompetencies: builder.query<
      ICompetencyApiResponse,
      { search?: string; subject?: string }
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params.search) queryParams.append('q', params.search);
        if (params.subject) queryParams.append('subject', params.subject);
        return `/lms/competencies?${queryParams.toString()}`;
      },
      providesTags: ['Competency'],
    }),

    // Assignments (for reference)
    getAssignments: builder.query<
      IAssignmentApiResponse,
      { search?: string; subject?: string }
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params.search) queryParams.append('q', params.search);
        if (params.subject) queryParams.append('subject', params.subject);
        return `/lms/assignments?${queryParams.toString()}`;
      },
      providesTags: ['Assignment'],
    }),

    // Learning Indicators (for reference)
    getLearningIndicators: builder.query<
      ILearningIndicatorApiResponse,
      { search?: string; subject?: string; substrandId?: number }
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params.search) queryParams.append('q', params.search);
        if (params.subject) queryParams.append('subject', params.subject);
        if (params.substrandId)
          queryParams.append('substrandId', params.substrandId.toString());
        return `/academics/learning-indicators?${queryParams.toString()}`;
      },
      providesTags: ['LearningIndicator'],
    }),

    // Resource management (keeping existing structure for now)
    getResources: builder.query<
      { id: number; url: string; name: string; type: string }[],
      number
    >({
      query: lessonNoteId =>
        `/academics/lesson-notes/${lessonNoteId}/resources`,
      providesTags: (_result, _error, lessonNoteId) => [
        { type: 'LessonNote', id: lessonNoteId },
        { type: 'Resource', id: lessonNoteId },
      ],
    }),

    uploadResource: builder.mutation<
      { id: number; url: string },
      { lessonNoteId: number; file: File }
    >({
      query: ({ lessonNoteId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/academics/lesson-notes/${lessonNoteId}/resources`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { lessonNoteId }) => [
        { type: 'LessonNote', id: lessonNoteId },
        { type: 'Resource', id: lessonNoteId },
      ],
    }),

    deleteResource: builder.mutation<
      void,
      { lessonNoteId: number; resourceId: number }
    >({
      query: ({ lessonNoteId, resourceId }) => ({
        url: `/academics/lesson-notes/${lessonNoteId}/resources/${resourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { lessonNoteId }) => [
        { type: 'LessonNote', id: lessonNoteId },
        { type: 'Resource', id: lessonNoteId },
      ],
    }),

    proposeLessonNoteImport: builder.mutation<
      ILessonNoteImportProposal,
      IImportProposalParams
    >({
      query: params => {
        if (params.file) {
          const formData = new FormData();
          if (params.lessonId !== undefined) {
            formData.append('lessonId', params.lessonId.toString());
          }
          if (params.workSchemeId !== undefined) {
            formData.append('workSchemeId', params.workSchemeId.toString());
          }
          if (params.body) {
            formData.append('body', params.body);
          }
          formData.append('file', params.file);
          return {
            url: '/academics/lesson-notes/import-proposal',
            method: 'POST',
            body: formData,
          };
        }

        const body: Record<string, any> = {};
        if (params.lessonId !== undefined) body.lessonId = params.lessonId;
        if (params.workSchemeId !== undefined)
          body.workSchemeId = params.workSchemeId;
        if (params.body !== undefined) body.body = params.body;

        return {
          url: '/academics/lesson-notes/import-proposal',
          method: 'POST',
          body,
        };
      },
    }),
  }),
});

export const {
  useGetLessonNotesQuery,
  useGetLessonNoteByIdQuery,
  useCreateLessonNoteMutation,
  useUpdateLessonNoteMutation,
  usePatchLessonNoteMutation,
  useCopyLessonNoteMutation,
  useUpdateLessonNoteContentMutation,
  useDeleteLessonNoteMutation,
  useGetLessonNotesSummaryQuery,
  useGetLessonLearningIndicatorsQuery,
  useAddLessonLearningIndicatorMutation,
  useRemoveLessonLearningIndicatorMutation,
  useGetLessonCompetenciesQuery,
  useAddLessonCompetencyMutation,
  useRemoveLessonCompetencyMutation,
  useGetLessonAssignmentsQuery,
  useAddLessonAssignmentMutation,
  useRemoveLessonAssignmentMutation,

  useGetLessonResourcesQuery,
  useAddLessonResourceMutation,
  useRemoveLessonResourceMutation,
  usePatchLessonResourceMutation,
  usePatchLessonAssignmentMutation,
  useGetOutlinesQuery,
  useGetCompetenciesQuery,
  useGetAssignmentsQuery,
  useGetLearningIndicatorsQuery,
  useGetResourcesQuery,
  useUploadResourceMutation,
  useDeleteResourceMutation,
  useProposeLessonNoteImportMutation,
} = LessonNotesApi;
